import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaPlus, FaTimes, FaTag, FaMagic, FaLock } from 'react-icons/fa';
import apiClient from '../../../api';
import { Card, CardTitle } from '../styles';
import { Button } from '../../../components/common/Button';

const Container = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InputGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Input = styled.input`
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.9rem;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tag = styled.span`
  background-color: ${props => props.isSystem ? '#e9ecef' : '#e3f2fd'};
  color: ${props => props.isSystem ? '#495057' : '#0d47a1'};
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid ${props => props.isSystem ? '#ced4da' : '#90caf9'};
`;

const RemoveIcon = styled(FaTimes)`
  cursor: pointer;
  opacity: 0.6;
  &:hover { opacity: 1; color: red; }
`;

const SectionLabel = styled.h5`
  margin: 0.5rem 0 0.25rem 0;
  font-size: 0.9rem;
  color: #666;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LimitBar = styled.div`
  background-color: #f0f0f0;
  border-radius: 4px;
  height: 6px;
  width: 100%;
  margin: 0.5rem 0;
  overflow: hidden;
`;

const LimitFill = styled.div`
  background-color: ${props => props.isFull ? '#e74c3c' : '#2ecc71'};
  height: 100%;
  width: ${props => props.percentage}%;
  transition: width 0.3s ease;
`;

const LimitText = styled.div`
  font-size: 0.8rem;
  color: ${props => props.isFull ? '#e74c3c' : '#666'};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const KeywordsManager = ({ user, limit }) => {
  const [customKeywords, setCustomKeywords] = useState([]);
  const [defaultKeywords, setDefaultKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [loading, setLoading] = useState(false);

  const currentUsage = customKeywords.length;
  const isLimitReached = currentUsage >= limit;
  const usagePercentage = Math.min((currentUsage / limit) * 100, 100);

  useEffect(() => {
    fetchKeywords();
  }, [user]);

  const fetchKeywords = async () => {
    if (!user?.cliente_id) return;
    try {
      const res = await apiClient.get(`/api/settings/keywords/${user.cliente_id}`);
      setCustomKeywords(res.data.custom);
      setDefaultKeywords(res.data.defaults);
    } catch (error) {
      console.error("Erro ao carregar keywords", error);
    }
  };

  const handleAdd = async () => {
    if (!newKeyword.trim()) return;
    setLoading(true);
    if (isLimitReached) {
        alert("Você atingiu o limite de palavras-chave do seu plano. Faça um upgrade para adicionar mais.");
        return;
    }
    try {
      const res = await apiClient.post(`/api/settings/keywords/${user.cliente_id}`, {
        keyword: newKeyword
      });
      setCustomKeywords([...customKeywords, { id: res.data.id, text: newKeyword, type: 'CUSTOM' }]);
      setNewKeyword('');
    } catch (error) {
      alert(error.response?.data?.error || "Erro ao adicionar.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (id) => {
    try {
      await apiClient.delete(`/api/settings/keywords/${user.cliente_id}?id=${id}`);
      setCustomKeywords(customKeywords.filter(k => k.id !== id));
    } catch (error) {
      console.error("Erro ao remover", error);
    }
  };

  const addSystemSuggestion = (word) => {
    setNewKeyword(word);
  };

  return (
    <Container>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <CardTitle style={{margin: 0}}><FaTag /> Palavras Monitoradas</CardTitle>
        {isLimitReached && <span style={{fontSize: '0.8rem', color: '#e74c3c', fontWeight: 'bold'}}><FaLock /> Limite Atingido</span>}
      </div>
      
      <div>
          <LimitText isFull={isLimitReached}>
              <span>Uso do Plano:</span>
              <span><strong>{currentUsage}</strong> / {limit}</span>
          </LimitText>
          <LimitBar>
              <LimitFill percentage={usagePercentage} isFull={isLimitReached} />
          </LimitBar>
      </div>

      <p style={{fontSize: '0.85rem', color: '#666'}}>
        Adicione termos para filtrar comentários e analisar sentimentos.
      </p>

      <InputGroup>
        <Input 
          placeholder={isLimitReached ? "Limite atingido" : "Ex: 'Preço alto', 'Entrega'..."}
          value={newKeyword}
          onChange={(e) => setNewKeyword(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
          disabled={isLimitReached}
        />
        <Button onClick={handleAdd} disabled={loading || !newKeyword || isLimitReached}>
          <FaPlus /> Adicionar
        </Button>
      </InputGroup>

      {/* Palavras do Cliente */}
      <SectionLabel>Minhas Palavras:</SectionLabel>
      <TagsContainer>
        {customKeywords.length > 0 ? (
          customKeywords.map(k => (
            <Tag key={k.id}>
              {k.text}
              <RemoveIcon onClick={() => handleRemove(k.id)} />
            </Tag>
          ))
        ) : (
          <span style={{color: '#999', fontSize: '0.8rem'}}>Nenhuma palavra definida.</span>
        )}
      </TagsContainer>

      <hr style={{margin: '1rem 0', borderColor: '#eee'}}/>

      {/* Sugestões do Sistema */}
      <SectionLabel><FaMagic /> Sugestões Inteligentes:</SectionLabel>
      <TagsContainer>
        {defaultKeywords.map(word => (
          <Tag 
            key={word} 
            isSystem 
            style={{cursor: 'pointer'}} 
            onClick={() => addSystemSuggestion(word)}
            title="Clique para usar esta palavra"
          >
            {word} <FaPlus size={10}/>
          </Tag>
        ))}
      </TagsContainer>
    </Container>
  );
};

export default KeywordsManager;